// G2-2 / G2-3: post-advance settlement helpers for the home surface.
//
// The digest is a purely UI-side snapshot diff — no worker sim change, no new
// letter. Before a "long advance" (a skip-ahead or a multi-day sim) we stash the
// current {record, injured, unread, goal progress} in sessionStorage; when the
// dashboard re-renders with post-sim data we diff against it and render a
// dismissible "这段时间" card. Single-day "继续推进" never snapshots, so it never
// produces a card. The card is transient by design: it lives in sessionStorage
// (survives a refresh mid-advance), is cleared on dismiss, and never touches the
// back stack or the worker.

// Long-advance option ids: multi-day sims + phase-spanning skips. Deliberately
// NOT the same as the 近/远 UI grouping — a multi-day playoff advance is UI-near
// but still a long advance, while a single-day "one day"/"one pick"/live-day
// step is not. Navigation-only entries (url) are excluded via isLongAdvanceOption.
export const LONG_ADVANCE_OPTION_IDS = new Set<string>([
  "week",
  "month",
  "untilAllStarGame",
  "untilTradeDeadline",
  "untilPlayoffs",
  "untilEndOfRound",
  "untilEndOfPlayIn",
  "throughPlayoffs",
  "untilDraft",
  "untilEnd",
  "untilResignPlayers",
  "untilFreeAgency",
  "untilPreseason",
  "untilRegularSeason",
]);

export const isLongAdvanceOption = (id: string, hasUrl: boolean): boolean =>
  !hasUrl && LONG_ADVANCE_OPTION_IDS.has(id);

export type AdvanceSnapshot = {
  lid: number;
  season: number;
  won: number;
  lost: number;
  injured: number;
  unread: number;
  goalProgress: number;
  ownerTrust?: number;
};

// m_ prefix family (shared convention with the ?m_open intent channel). This is
// only the namespace/legacy key; production snapshots always use the lid-scoped
// key returned by getAdvanceSnapshotKey. The legacy unscoped payload is never
// read, so an old A-league snapshot cannot be compared with league B after an
// upgrade.
export const ADVANCE_SNAPSHOT_KEY = "m_advance_snapshot";

export const getAdvanceSnapshotKey = (lid: number): string => `${ADVANCE_SNAPSHOT_KEY}:${lid}`;

const getSessionStorage = (): Storage | undefined => {
  try {
    if (typeof window === "undefined" || !window.sessionStorage) {
      return undefined;
    }
    return window.sessionStorage;
  } catch {
    // Access can throw in privacy modes / sandboxed frames.
    return undefined;
  }
};

const validLid = (lid: number): boolean => Number.isInteger(lid) && lid >= 0;

export const readAdvanceSnapshot = (lid: number): AdvanceSnapshot | undefined => {
  if (!validLid(lid)) {
    return undefined;
  }
  const storage = getSessionStorage();
  if (!storage) {
    return undefined;
  }
  try {
    // Fail closed on the pre-isolation payload. Removing it is migration cleanup;
    // it is never treated as belonging to whichever league happens to be open.
    storage.removeItem(ADVANCE_SNAPSHOT_KEY);
    const raw = storage.getItem(getAdvanceSnapshotKey(lid));
    if (!raw) {
      return undefined;
    }
    const parsed = JSON.parse(raw) as Partial<AdvanceSnapshot>;
    if (
      typeof parsed.lid !== "number" ||
      parsed.lid !== lid ||
      typeof parsed.season !== "number" ||
      typeof parsed.won !== "number" ||
      typeof parsed.lost !== "number" ||
      typeof parsed.injured !== "number" ||
      typeof parsed.unread !== "number" ||
      typeof parsed.goalProgress !== "number"
    ) {
      return undefined;
    }
    return {
      lid: parsed.lid,
      season: parsed.season,
      won: parsed.won,
      lost: parsed.lost,
      injured: parsed.injured,
      unread: parsed.unread,
      goalProgress: parsed.goalProgress,
      ownerTrust: typeof parsed.ownerTrust === "number" ? parsed.ownerTrust : undefined,
    };
  } catch {
    return undefined;
  }
};

export const writeAdvanceSnapshot = (snapshot: AdvanceSnapshot): void => {
  if (!validLid(snapshot.lid)) {
    return;
  }
  const storage = getSessionStorage();
  if (!storage) {
    return;
  }
  try {
    storage.removeItem(ADVANCE_SNAPSHOT_KEY);
    storage.setItem(getAdvanceSnapshotKey(snapshot.lid), JSON.stringify(snapshot));
  } catch {
    // Quota / privacy failures degrade to "no digest" — acceptable, transient.
  }
};

export const clearAdvanceSnapshot = (lid: number): void => {
  if (!validLid(lid)) {
    return;
  }
  const storage = getSessionStorage();
  if (!storage) {
    return;
  }
  try {
    // Deliberately remove only this league's baseline. Another league in the
    // same tab keeps its pending digest; a duplicated tab has its own copied,
    // subsequently independent sessionStorage browsing context.
    storage.removeItem(getAdvanceSnapshotKey(lid));
  } catch {
    // ignore
  }
};

export type AdvanceDigest = {
  // Record gained over the stretch (only within the same season, only when
  // wins/losses moved forward — a season rollover resets them and is suppressed).
  record?: { wonDelta: number; lostDelta: number };
  // Signed change in injured count (positive = new injuries, negative = healed).
  injuredDelta?: number;
  // New unread letters that arrived (never negative).
  newLetters?: number;
  // Signed change in season-goal progress percent.
  goalDelta?: number;
  // Signed movement in the owner's combined evaluation score.
  ownerTrustDelta?: number;
};

// Diff two snapshots into the rows the card renders. Rows with no change are
// omitted ("无差异的行不渲染"); if nothing changed, returns undefined so the whole
// card is suppressed ("全部无差异则整卡不出").
export const computeAdvanceDigest = (
  before: AdvanceSnapshot,
  after: AdvanceSnapshot,
): AdvanceDigest | undefined => {
  // Defense in depth: even a caller holding stale React state cannot compare a
  // snapshot from league A with current data from league B.
  if (before.lid !== after.lid) {
    return undefined;
  }
  const sameSeason = before.season === after.season;
  const digest: AdvanceDigest = {};

  // Record + goal are season-relative; suppress them across a season boundary
  // (won/lost/goal reset), where a delta would be meaningless or negative.
  if (sameSeason && after.won >= before.won && after.lost >= before.lost) {
    const wonDelta = after.won - before.won;
    const lostDelta = after.lost - before.lost;
    if (wonDelta > 0 || lostDelta > 0) {
      digest.record = { wonDelta, lostDelta };
    }
  }

  if (sameSeason) {
    const injuredDelta = after.injured - before.injured;
    if (injuredDelta !== 0) {
      digest.injuredDelta = injuredDelta;
    }
    const goalDelta = after.goalProgress - before.goalProgress;
    if (goalDelta !== 0) {
      digest.goalDelta = goalDelta;
    }
    if (before.ownerTrust !== undefined && after.ownerTrust !== undefined) {
      const ownerTrustDelta = after.ownerTrust - before.ownerTrust;
      if (Math.abs(ownerTrustDelta) > 0.001) {
        digest.ownerTrustDelta = ownerTrustDelta;
      }
    }
  }

  // New letters are meaningful across a season boundary too (new-season owner
  // mail). Note: the home view refreshes unread on newPhase/firstRun, so a
  // same-phase multi-week sim can under-count new letters — a graceful
  // degradation, never an over-count.
  const newLetters = Math.max(0, after.unread - before.unread);
  if (newLetters > 0) {
    digest.newLetters = newLetters;
  }

  const hasAnyRow =
    digest.record !== undefined ||
    digest.injuredDelta !== undefined ||
    digest.newLetters !== undefined ||
    digest.goalDelta !== undefined ||
    digest.ownerTrustDelta !== undefined;

  return hasAnyRow ? digest : undefined;
};

// G2-3: the "今日完成" stop point. The main task card switches to the day-done
// state only when the matchday IS the loop (the phase task routes to the
// matchday), today has no game left to play (the user's next game is a future
// matchday), and nothing dangerous is waiting in the queue. Any danger item or a
// game still to play today keeps the normal behavior.
export const getDashboardDayDone = ({
  hasDanger,
  matchdayIsLoop,
  nextGameDaysUntil,
}: {
  hasDanger: boolean;
  matchdayIsLoop: boolean;
  nextGameDaysUntil: number | undefined;
}): boolean =>
  matchdayIsLoop && !hasDanger && nextGameDaysUntil !== undefined && nextGameDaysUntil > 0;
